import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { User, LogOut, Library, Settings, Menu, X } from 'lucide-react';

export const Navbar = () => {
    const { user, isAuthenticated, isAdmin, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { href: '/courses', label: 'Courses' },
        { href: '/workshops', label: 'Painting Videos' },
        { href: '/sculptures', label: 'Sculpture Videos' },
        { href: '/about', label: 'About' },
    ];

    const isActive = (path) => location.pathname === path;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border" data-testid="navbar">
            <div className="container-main">
                <div className="flex items-center justify-between h-24">
                    {/* Logo */}
                    <Link to="/" className="flex items-center" data-testid="nav-logo">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_video-rebuild-2/artifacts/o1tpbfck_Zarolla_logo_final_color.png" 
                            alt="Zarolla Online Art Academy" 
                            className="h-20"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                                className={`text-sm font-medium transition-colors hover:text-accent ${
                                    isActive(link.href)
                                        ? 'text-accent border-b-2 border-accent pb-1'
                                        : 'text-muted-foreground'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Right Section */}
                    <div className="hidden md:flex items-center gap-4">
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="gap-2"
                                        data-testid="user-menu-trigger"
                                    >
                                        <User className="h-4 w-4" />
                                        <span className="max-w-[150px] truncate">
                                            {user?.email}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem asChild>
                                        <Link to="/profile" className="flex items-center gap-2" data-testid="nav-profile">
                                            <User className="h-4 w-4" />
                                            My Account
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/my-library" className="flex items-center gap-2" data-testid="nav-my-library">
                                            <Library className="h-4 w-4" />
                                            My Library
                                        </Link>
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                                                    <Settings className="h-4 w-4" />
                                                    Admin Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 text-destructive"
                                        data-testid="nav-logout"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Log Out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button asChild data-testid="nav-login-btn">
                                <Link to="/login">Sign In</Link>
                            </Button>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {mobileMenuOpen ? (
                            <X className="h-6 w-6" />
                        ) : (
                            <Menu className="h-6 w-6" />
                        )}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-border">
                        <div className="flex flex-col gap-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`text-sm font-medium px-2 py-2 rounded ${
                                        isActive(link.href)
                                            ? 'text-accent bg-accent/10'
                                            : 'text-muted-foreground'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            {isAuthenticated ? (
                                <>
                                    <Link
                                        to="/profile"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="text-sm font-medium px-2 py-2"
                                    >
                                        My Account
                                    </Link>
                                    <Link
                                        to="/my-library"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="text-sm font-medium px-2 py-2"
                                    >
                                        My Library
                                    </Link>
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="text-sm font-medium px-2 py-2"
                                        >
                                            Admin Dashboard
                                        </Link>
                                    )}
                                    <button
                                        onClick={() => {
                                            handleLogout();
                                            setMobileMenuOpen(false);
                                        }}
                                        className="text-sm font-medium px-2 py-2 text-left text-destructive"
                                    >
                                        Log Out
                                    </button>
                                </>
                            ) : (
                                <Link
                                    to="/login"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="text-sm font-medium px-2 py-2"
                                >
                                    Sign In
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
